import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import { DM_RETENTION_DAYS, type MessageDTO, type MoveRequestDTO } from "@justone/shared";
import { errorMessage } from "../api/http";
import { useChannels, useConversations, useDecideMove, useMe, useMessages, useMoveRequests, useRequestMove, useUserMap } from "../api/queries";
import { setViewingRoom } from "../api/realtime";
import { Composer } from "../components/Composer";
import { MessageList } from "../components/MessageList";
import { Avatar, Dialog, Field, Icon, useToast } from "../components/ui";
import { formatRemaining } from "../lib/format";
import { dmView } from "../lib/room";
import type { ThreadOutlet } from "./ChannelPage";
import type { WorkspaceOutlet } from "./Workspace";

/** 업무 이야기로 보이는 말 (PRD 5.4) */
const WORK_WORDS = ["할일", "할 일", "마감", "까지", "일정", "결정", "회의", "견적", "보고"];
const HOUR = 3_600_000;

/** 채널로 옮기자고 제안할지: 업무 키워드, 긴 메시지(300자), 1시간에 15개 이상 */
function looksLikeWork(messages: MessageDTO[]): boolean {
  const recent = messages.filter((m) => m.kind === "user" && !m.deleted && Date.now() - new Date(m.createdAt).getTime() < HOUR);
  if (recent.length >= 15) return true;
  return recent.some((m) => m.body.length >= 300 || WORK_WORDS.some((w) => m.body.includes(w)));
}

const suggestKey = (id: number) => `justone:dm-suggest:${id}`;
const today = () => new Date().toDateString();

function suggestionDismissedToday(id: number): boolean {
  try {
    return localStorage.getItem(suggestKey(id)) === today();
  } catch {
    return false;
  }
}

function dismissSuggestion(id: number) {
  try {
    localStorage.setItem(suggestKey(id), today());
  } catch {
    // 개인 창 등 저장소를 못 쓰면 이번 화면에서만 닫힌다
  }
}

export function DMPage() {
  const { conversationId } = useParams();
  const id = Number(conversationId);
  const conversations = useConversations();
  const conv = conversations.data?.find((c) => c.id === id);
  const me = useMe();
  const users = useUserMap();
  const outlet = useOutletContext<WorkspaceOutlet>();
  const navigate = useNavigate();
  const { rootId } = useParams();
  const [moveFrom, setMoveFrom] = useState<number | null>(null);
  const [suggestHidden, setSuggestHidden] = useState(() => suggestionDismissedToday(id));
  const messages = useMessages({ kind: "dm", id });
  const loaded = useMemo(() => (messages.data ? messages.data.pages.slice().reverse().flatMap((p) => p.messages) : []), [messages.data]);

  const [readMarker, setReadMarker] = useState<{ id: number; marker: number | null } | null>(null);
  useEffect(() => {
    if (conv && readMarker?.id !== conv.id) setReadMarker({ id: conv.id, marker: conv.unread > 0 ? conv.lastReadMessageId : null });
  }, [conv, readMarker]);

  useEffect(() => {
    setViewingRoom({ kind: "dm", id });
    setSuggestHidden(suggestionDismissedToday(id));
    return () => setViewingRoom(null);
  }, [id]);

  const openThread = useCallback((root: number) => navigate(`/d/${id}/t/${root}`), [navigate, id]);

  if (conversations.data && !conv) return <Navigate to="/" replace />;
  if (!conv || readMarker?.id !== conv.id) return null;

  const room = dmView(conv, users, me);
  const others = conv.memberIds.filter((m) => m !== me.id);
  const firstRecent = loaded.find((m) => m.kind === "user" && !m.deleted && Date.now() - new Date(m.createdAt).getTime() < 24 * HOUR) ?? loaded.find((m) => m.kind === "user" && !m.deleted);
  const showSuggestion = !suggestHidden && looksLikeWork(loaded);

  return (
    <div className={`channel-layout${rootId ? " with-thread" : ""}`}>
      <section className="channel-col" aria-label={room.title}>
        <header className="topbar">
          <button type="button" className="icon-btn menu-toggle" onClick={outlet.openNav} aria-label="채널 목록 열기">
            <Icon name="menu" />
          </button>
          <span className="dm-faces">
            {others.slice(0, 3).map((uid) => (
              <Avatar key={uid} user={users.get(uid)} size={24} />
            ))}
          </span>
          <h1>{room.title}</h1>
          <span className="topic">메시지는 {DM_RETENTION_DAYS}일 후 사라집니다</span>
          <div className="actions">
            <button type="button" className="btn btn-sm" disabled={!firstRecent} onClick={() => firstRecent && setMoveFrom(firstRecent.id)}>
              채널로 옮기기
            </button>
          </div>
        </header>
        <MoveBanner conversationId={id} meId={me.id} memberCount={conv.memberIds.length} />
        {showSuggestion && (
          <div className="dm-suggest" role="note">
            <span>업무 이야기 같아요. 채널로 옮기면 다른 사람도 보고 기록도 남아요.</span>
            <button type="button" className="btn btn-primary btn-sm" disabled={!firstRecent} onClick={() => firstRecent && setMoveFrom(firstRecent.id)}>
              채널로 옮기기
            </button>
            <button
              type="button"
              className="btn btn-sm btn-quiet"
              onClick={() => {
                dismissSuggestion(id);
                setSuggestHidden(true);
              }}
            >
              닫기
            </button>
          </div>
        )}
        <MessageList key={`dm-${id}`} room={room} users={users} me={me} readMarker={readMarker.marker} onOpenThread={openThread} onMoveFrom={setMoveFrom} />
        <Composer key={`dm-composer-${id}`} room={room} />
      </section>
      <Outlet context={{ ...outlet, room } satisfies ThreadOutlet} />
      {moveFrom !== null && <MoveDialog conversationId={id} fromMessageId={moveFrom} messages={loaded} onClose={() => setMoveFrom(null)} />}
    </div>
  );
}

/** 대기 중인 옮기기 요청: 승인/거절 또는 기다리는 중 */
function MoveBanner({ conversationId, meId, memberCount }: { conversationId: number; meId: number; memberCount: number }) {
  const q = useMoveRequests(conversationId);
  const decide = useDecideMove(conversationId);
  const users = useUserMap();
  const { data: channels = [] } = useChannels();
  const toast = useToast();
  const r: MoveRequestDTO | undefined = q.data?.find((x) => x.status === "pending");
  if (!r) return null;
  const channelName = channels.find((c) => c.id === r.targetChannelId)?.name ?? "채널";
  const requester = users.get(r.requestedById)?.displayName ?? "누군가";
  const decided = r.approvedBy.includes(meId);
  const act = (approve: boolean) =>
    decide.mutate({ id: r.id, approve }, { onSuccess: (res) => toast(res.status === "approved" ? `#${channelName}로 옮겼습니다.` : approve ? "승인했습니다." : "거절했습니다."), onError: (e) => toast(errorMessage(e)) });
  return (
    <div className="move-banner" role="status">
      {decided ? (
        <span>
          메시지 {r.messageCount}개를 #{channelName}로 옮기는 중입니다. 승인 {r.approvedBy.length}/{memberCount}, {formatRemaining(r.expiresAt)}
        </span>
      ) : (
        <>
          <span>
            {requester}님이 메시지 {r.messageCount}개를 #{channelName}로 옮기자고 합니다. 모두 승인해야 옮겨집니다.
          </span>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => act(true)} disabled={decide.isPending}>
            승인
          </button>
          <button type="button" className="btn btn-sm" onClick={() => act(false)} disabled={decide.isPending}>
            거절
          </button>
        </>
      )}
    </div>
  );
}

function MoveDialog({ conversationId, fromMessageId, messages, onClose }: { conversationId: number; fromMessageId: number; messages: MessageDTO[]; onClose: () => void }) {
  const { data: channels = [] } = useChannels();
  const targets = channels.filter((c) => c.joined && !c.archived);
  const [channelId, setChannelId] = useState(() => targets.find((c) => c.isDefault)?.id ?? targets[0]?.id ?? 0);
  const request = useRequestMove(conversationId);
  const toast = useToast();
  const range = messages.filter((m) => m.kind === "user" && !m.deleted && m.parentId === null && m.id >= fromMessageId);
  const last = range.at(-1);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!last) return;
    request.mutate(
      { fromMessageId, toMessageId: last.id, channelId },
      {
        onSuccess: (r) => {
          toast(r.status === "approved" ? "채널로 옮겼습니다." : "다른 참여자에게 승인을 요청했습니다.");
          onClose();
        },
        onError: (err) => toast(errorMessage(err)),
      },
    );
  };

  return (
    <Dialog open onClose={onClose} label="채널로 옮기기">
      <form onSubmit={submit}>
        <h2>채널로 옮기기</h2>
        <p style={{ marginTop: 0, color: "var(--ink-2)" }}>
          고른 메시지부터 마지막 메시지까지 <strong>{range.length}개</strong>를 채널에 올립니다. 대화에 참여한 사람 모두가 승인해야 옮겨지고, 그동안 이 메시지들은 지워지지 않습니다.
        </p>
        <Field id="move-channel" label="옮길 채널">
          <select id="move-channel" className="input" value={channelId} onChange={(e) => setChannelId(Number(e.target.value))}>
            {targets.map((c) => (
              <option key={c.id} value={c.id}>
                #{c.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="buttons">
          <button type="button" className="btn" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={!last || !channelId || request.isPending}>
            승인 요청
          </button>
        </div>
      </form>
    </Dialog>
  );
}
